/**
 * Shared gauge geometry and parsing helpers.
 *
 * Extracted so every gauge on the statistics page draws against one set of
 * dimensions and one polar-coordinate implementation, instead of each chart
 * carrying its own copy.
 */

export const WIDTH = 500;
export const HEIGHT = 280;
export const CX = WIDTH / 2;
export const CY = HEIGHT - 30;
export const OUTER_R = 210;
export const INNER_R = 120;
export const START_ANGLE = -Math.PI;
export const END_ANGLE = 0;

/**
 * Parse a backend number that may use a comma as decimal separator.
 */
export function parseLocaleNumber(raw, fallback = 0) {
  if (raw == null || raw === '') {
    return fallback;
  }

  const parsed = parseFloat(String(raw).replace(',', '.'));
  return Number.isNaN(parsed) ? fallback : parsed;
}

export function polarToXY(angle, r) {
  return { x: CX + r * Math.cos(angle), y: CY + r * Math.sin(angle) };
}

/**
 * Map a value in [min, max] onto the gauge arc.
 */
export function valueToAngleForRange(v, min, max, mirrored = false) {
  const position = (v - min) / (max - min);
  return mirrored
    ? END_ANGLE - position * (END_ANGLE - START_ANGLE)
    : START_ANGLE + position * (END_ANGLE - START_ANGLE);
}

/**
 * Map a segment index onto the arc, for gauges whose segments are drawn at
 * equal arc width regardless of the value range they cover.
 */
export function segmentIndexToAngle(index, segmentCount) {
  return START_ANGLE + (index / segmentCount) * (END_ANGLE - START_ANGLE);
}

export function arcPath(startAngle, endAngle, outerR, innerR) {
  const s1 = polarToXY(startAngle, outerR);
  const e1 = polarToXY(endAngle, outerR);
  const s2 = polarToXY(endAngle, innerR);
  const e2 = polarToXY(startAngle, innerR);
  const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;
  return [
    `M ${s1.x} ${s1.y}`,
    `A ${outerR} ${outerR} 0 ${largeArc} 1 ${e1.x} ${e1.y}`,
    `L ${s2.x} ${s2.y}`,
    `A ${innerR} ${innerR} 0 ${largeArc} 0 ${e2.x} ${e2.y}`,
    'Z',
  ].join(' ');
}

export function needlePath(angle, length, baseWidth) {
  const tip = polarToXY(angle, length);
  const left = polarToXY(angle + Math.PI / 2, baseWidth);
  const right = polarToXY(angle - Math.PI / 2, baseWidth);
  const tail = polarToXY(angle + Math.PI, baseWidth * 2);
  return `M ${left.x} ${left.y} L ${tip.x} ${tip.y} L ${right.x} ${right.y} L ${tail.x} ${tail.y} Z`;
}

/**
 * Map a needle value to a left-to-right arc position using a non-uniform,
 * label-based scale: scaleValues[i] sits at arc position (i + 1) / segmentCount.
 *
 * Values at or below the first label snap to the end of the first segment;
 * values beyond the last label clamp to the end of the labelled range.
 */
export function needleValueToPosition(v, scaleValues, segmentCount) {
  for (let i = 0; i < scaleValues.length; i++) {
    if (v <= scaleValues[i]) {
      const rightPos = (i + 1) / segmentCount;
      if (i === 0) {
        return rightPos;
      }
      const rightVal = scaleValues[i];
      const leftVal = scaleValues[i - 1];
      const leftPos = i / segmentCount;
      const t = (v - leftVal) / (rightVal - leftVal);
      return leftPos + t * (rightPos - leftPos);
    }
  }
  return scaleValues.length / segmentCount;
}

export function needleValueToAngle(v, scaleValues, segmentCount) {
  const position = needleValueToPosition(v, scaleValues, segmentCount);
  return START_ANGLE + position * (END_ANGLE - START_ANGLE);
}

/**
 * Format a share: whole numbers bare, otherwise one decimal with a comma.
 * No % sign - the page explains above the gauges that the numbers are shares.
 */
export function formatPercentage(value) {
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded)
    ? String(rounded)
    : rounded.toFixed(1).replace('.', ',');
}

/**
 * Format a weight the way the backend presents it: one decimal, comma separator.
 */
export function formatWeight(value) {
  return value.toFixed(1).replace('.', ',');
}

/**
 * Is a footer value empty of information?
 *
 * Footer values are passed through as the backend spells them, so a zero can
 * arrive as '0', '0,0', '0,00' or ''. Rows like these carry nothing and are left
 * off the footer.
 */
export function isBlankFooterValue(value) {
  if (value == null || String(value).trim() === '') {
    return true;
  }
  return parseLocaleNumber(value, 0) === 0;
}
